import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMoon, FiSun } from 'react-icons/fi';
import logo from '../images/Coltigent_Logo.png';
import { useTheme } from '../context/ThemeContext';

const Navbar = () => {
  const { theme, setTheme, isDark: darkMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [, setActiveDropdown] = useState(null);
  const [servicesHover, setServicesHover] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();
  const navbarRef = useRef(null);

  const toggleTheme = () => setTheme(theme === 'dark' ? 'light' : 'dark');

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close navbar when route changes
  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
    setMobileServicesOpen(false);
  }, [location]);

  // Close navbar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setIsOpen(false);
        setActiveDropdown(null);
        setServicesHover(false);
        setMobileServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleNavbar = () => {
    setIsOpen(!isOpen);
    setActiveDropdown(null);
    if (isOpen) setMobileServicesOpen(false);
  };

  const mainNavItems = [
    { path: '/about', label: 'Who we are', icon: '👥' },
    { path: '/case-studies', label: 'Case Studies' },
    { path: '/insight', label: 'Insights', icon: '💡' },
    { path: '/careers', label: 'Careers' },
    { path: '/contact', label: 'Contact' },
  ];

  const serviceItems = [
    {
      path: "/services/ai-product-engineering",
      label: "AI Product Engineering",
      description: "GenAI, Agentic AI, RAG & AI Copilots",
      column: 1,
    },
    {
      path: "/services/modern-data-platforms",
      label: "Cloud & Data Platforms",
      description: "AWS, APIs, Data Engineering & Analytics",
      column: 2,
    },
    {
      path: "/services/saas",
      label: "Custom SaaS & Enterprise Software",
      description: "Multi-tenant SaaS, MVPs & Enterprise Applications",
      column: 3,
    },
  ];

  const isActive = (path) => location.pathname === path;
  const isServicesActive = location.pathname === '/services' || location.pathname.startsWith('/services/');

  // Enterprise navbar: light = glass (transparent white + blur), dark = solid navy + white text
  const navBg = darkMode
    ? (isScrolled ? 'rgba(11, 18, 32, 0.88)' : 'rgba(11, 18, 32, 0.72)')
    : (isScrolled ? 'rgba(255, 255, 255, 0.92)' : 'rgba(255, 255, 255, 0.78)');
  const navText = darkMode ? '#ffffff' : '#0F172A';
  const navAccent = darkMode ? '#93c5fd' : '#2563eb';
  const navBlur = isScrolled ? 'blur(16px) saturate(180%)' : 'blur(14px) saturate(160%)';
  const navShadow = isScrolled
    ? (darkMode ? '0 1px 0 rgba(255,255,255,0.06), 0 2px 8px rgba(0,0,0,0.2)' : '0 1px 0 rgba(15,23,42,0.04), 0 2px 8px rgba(15,23,42,0.06)')
    : 'none';

  return (
    <motion.nav 
      ref={navbarRef}
      className="navbar navbar-expand-lg fixed-top site-navbar"
      style={{
        height: isScrolled ? '68px' : '76px',
        minHeight: isScrolled ? '68px' : '72px',
        maxHeight: isScrolled ? '68px' : '80px',
        position: 'sticky',
        top: 0,
        zIndex: 1030,
        background: navBg,
        backdropFilter: navBlur,
        WebkitBackdropFilter: navBlur,
        boxShadow: navShadow,
        transition: 'height 0.3s ease, background 0.4s ease, backdrop-filter 0.4s ease, box-shadow 0.3s ease, color 0.4s ease'
      }}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <style>{`
        .navbar a:focus,
        .navbar a:focus-visible,
        .navbar a:active,
        .navbar button:focus,
        .navbar button:focus-visible,
        .navbar button:active {
          outline: none !important;
          box-shadow: none !important;
        }
      `}</style>
      <div className="container-fluid d-flex align-items-center site-navbar-inner" style={{ height: '100%' }}>
        {/* Logo + Brand - vertically centered in navbar */}
        <Link 
          to="/" 
          className="navbar-brand d-flex align-items-center gap-2 text-decoration-none"
          style={{ 
            color: navText,
            fontWeight: '600',
            letterSpacing: '0.01em',
            cursor: 'pointer',
            transition: 'color 0.4s ease',
            height: '100%',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <img
            src={logo}
            alt="Coltigent Technology Services Pvt Ltd. Logo"
            className="d-inline-block align-top rounded-circle site-navbar-logo"
            style={{ width: 'auto' }}
          />
          <motion.span 
            className="fw-bold ms-2 ms-md-3 d-none d-md-inline"
            whileHover={{ opacity: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            Coltigent Technology Services Pvt Ltd.
          </motion.span>
          <motion.span 
            className="fw-bold ms-2 d-md-none"
            whileHover={{ opacity: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            Coltigent
          </motion.span>
        </Link>

        {/* Mobile Toggle Button */}
        <motion.button
          className="navbar-toggler border-0 d-lg-none"
          type="button"
          onClick={toggleNavbar}
          aria-controls="navbarContent"
          aria-expanded={isOpen}
          aria-label="Toggle navigation"
          whileTap={{ scale: 0.95 }}
          style={{ 
            minWidth: '44px', 
            minHeight: '44px',
            padding: '0.5rem',
            backgroundColor: 'transparent',
            border: 'none',
            outline: 'none'
          }}
        >
          <motion.div
            className="hamburger"
            animate={{ rotate: isOpen ? 45 : 0 }}
            transition={{ duration: 0.3 }}
            style={{
              width: '24px',
              height: '2px',
              backgroundColor: navText,
              position: 'relative',
              transformOrigin: 'center',
              transition: 'background-color 0.4s ease'
            }}
          >
            <motion.div
              style={{
                width: '24px',
                height: '2px',
                backgroundColor: navText,
                position: 'absolute',
                top: isOpen ? '0' : '-8px',
                left: '0',
                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                transition: 'all 0.3s ease, background-color 0.4s ease'
              }}
            />
            <motion.div
              style={{
                width: '24px',
                height: '2px',
                backgroundColor: navText,
                position: 'absolute',
                top: isOpen ? '0' : '8px',
                left: '0',
                transform: isOpen ? 'rotate(-90deg)' : 'rotate(0deg)',
                transition: 'all 0.3s ease, background-color 0.4s ease'
              }}
            />
          </motion.div>
        </motion.button>

        {/* Navbar Links */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              className="collapse navbar-collapse show"
              id="navbarContent"
              initial={{ opacity: 0, height: 0, y: -20 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -20 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                right: '0',
                zIndex: 1000,
                backgroundColor: darkMode ? 'rgb(26, 32, 44)' : '#ffffff',
                backdropFilter: 'blur(10px)',
                borderRadius: '0 0 0.5rem 0.5rem',
                boxShadow: darkMode ? '0 4px 20px rgba(0, 0, 0, 0.3)' : '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: darkMode ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(31, 106, 225, 0.1)',
                borderTop: 'none'
              }}
            >
              <ul className="navbar-nav ms-auto mb-2 mb-lg-0 d-flex flex-column w-100" style={{ padding: '1rem' }}>
                {/* Theme toggle (mobile) */}
                <li className="nav-item mb-2" style={{ padding: '0.5rem 0' }}>
                  <button
                    type="button"
                    className={`theme-toggle-btn ${darkMode ? 'is-dark' : 'is-light'}`}
                    aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                    onClick={toggleTheme}
                  >
                    {darkMode ? <FiMoon aria-hidden="true" /> : <FiSun aria-hidden="true" />}
                  </button>
                </li>
                {/* Main Navigation Items: Who we are, then What we do, then Case Studies, Insights, Careers, Contact */}
                {mainNavItems[0] && (
                  <motion.li 
                    key={mainNavItems[0].path}
                    className="nav-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0 }}
                  >
                    <Link 
                      className={`nav-link position-relative ${isActive(mainNavItems[0].path) ? 'active fw-bold' : ''}`}
                      to={mainNavItems[0].path}
                      onClick={() => setIsOpen(false)}
                      style={{
                        padding: '0.75rem 1rem',
                        borderRadius: '0.5rem',
                        marginBottom: '0.25rem',
                        transition: 'all 0.2s ease',
                        backgroundColor: isActive(mainNavItems[0].path) ? (darkMode ? 'rgba(147, 197, 253, 0.1)' : 'rgba(31, 106, 225, 0.1)') : 'transparent',
                        textDecoration: 'none',
                        color: isActive(mainNavItems[0].path) ? (darkMode ? '#93c5fd' : '#1F6AE1') : (darkMode ? '#e2e8f0' : '#1E1E1E'),
                        fontWeight: isActive(mainNavItems[0].path) ? '600' : '400',
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive(mainNavItems[0].path)) {
                          e.target.style.backgroundColor = darkMode ? 'rgba(147, 197, 253, 0.05)' : 'rgba(31, 106, 225, 0.05)';
                          e.target.style.color = darkMode ? '#93c5fd' : '#1F6AE1';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive(mainNavItems[0].path)) {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = darkMode ? '#e2e8f0' : '#1E1E1E';
                        }
                      }}
                    >
                      {mainNavItems[0].label}
                      <motion.span
                        className="position-absolute bottom-0 left-0 right-0"
                        initial={{ scaleX: isActive(mainNavItems[0].path) ? 1 : 0 }}
                        whileHover={{ scaleX: 1 }}
                        transition={{ duration: 0.175, ease: 'easeOut' }}
                        style={{ transformOrigin: 'left', backgroundColor: darkMode ? '#93c5fd' : '#1F6AE1', height: '2px' }}
                      />
                    </Link>
                  </motion.li>
                )}
                {/* What we do — mobile dropdown */}
                <motion.li
                  className="nav-item"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <button
                    type="button"
                    className={`nav-link position-relative w-100 text-start border-0 bg-transparent d-flex align-items-center justify-content-between ${isServicesActive ? 'active fw-bold' : ''}`}
                    onClick={() => setMobileServicesOpen((open) => !open)}
                    aria-expanded={mobileServicesOpen}
                    aria-haspopup="true"
                    style={{
                      padding: '0.75rem 1rem',
                      borderRadius: '0.5rem',
                      marginBottom: '0.25rem',
                      transition: 'all 0.2s ease',
                      backgroundColor: isServicesActive ? (darkMode ? 'rgba(147, 197, 253, 0.1)' : 'rgba(31, 106, 225, 0.1)') : 'transparent',
                      color: isServicesActive ? (darkMode ? '#93c5fd' : '#1F6AE1') : (darkMode ? '#e2e8f0' : '#1E1E1E'),
                      fontWeight: isServicesActive ? '600' : '400',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                    }}
                  >
                    What we do
                    <span style={{ opacity: 0.7, marginLeft: '0.5rem' }}>{mobileServicesOpen ? '−' : '+'}</span>
                  </button>
                  <AnimatePresence>
                    {mobileServicesOpen && (
                      <motion.ul
                        className="list-unstyled mb-2 ps-3"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {serviceItems.map((service) => (
                          <li key={service.path}>
                            <Link
                              to={service.path}
                              onClick={() => {
                                setIsOpen(false);
                                setMobileServicesOpen(false);
                              }}
                              className="d-block py-2 text-decoration-none"
                              style={{
                                color: isActive(service.path) ? (darkMode ? '#93c5fd' : '#1F6AE1') : (darkMode ? '#cbd5e1' : '#475569'),
                                fontSize: '0.9rem',
                                fontWeight: isActive(service.path) ? '600' : '400',
                              }}
                            >
                              {service.label}
                            </Link>
                          </li>
                        ))}
                        <li>
                          <Link
                            to="/services"
                            onClick={() => {
                              setIsOpen(false);
                              setMobileServicesOpen(false);
                            }}
                            className="d-block py-2 text-decoration-none fw-semibold"
                            style={{
                              color: darkMode ? '#93c5fd' : '#2563eb',
                              fontSize: '0.9rem',
                            }}
                          >
                            All Services
                          </Link>
                        </li>
                      </motion.ul>
                    )}
                  </AnimatePresence>
                </motion.li>
                {/* Case Studies, Insights, Careers, Contact */}
                {mainNavItems.slice(1).map((item, index) => (
                  <motion.li 
                    key={item.path}
                    className="nav-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: (index + 2) * 0.1 }}
                  >
                    <Link 
                        className={`nav-link position-relative ${
                          isActive(item.path) ? 'active fw-bold' : ''
                        }`}
                        to={item.path}
                        onClick={() => setIsOpen(false)}
                        style={{
                          padding: '0.75rem 1rem',
                          borderRadius: '0.5rem',
                          marginBottom: '0.25rem',
                          transition: 'all 0.2s ease',
                          backgroundColor: isActive(item.path) ? (darkMode ? 'rgba(147, 197, 253, 0.1)' : 'rgba(31, 106, 225, 0.1)') : 'transparent',
                          textDecoration: 'none',
                          color: isActive(item.path) ? (darkMode ? '#93c5fd' : '#1F6AE1') : (darkMode ? '#e2e8f0' : '#1E1E1E'),
                          fontWeight: isActive(item.path) ? '600' : '400',
                          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif"
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive(item.path)) {
                            e.target.style.backgroundColor = darkMode ? 'rgba(147, 197, 253, 0.05)' : 'rgba(31, 106, 225, 0.05)';
                            e.target.style.color = darkMode ? '#93c5fd' : '#1F6AE1';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive(item.path)) {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.color = darkMode ? '#e2e8f0' : '#1E1E1E';
                          }
                        }}
                      >
                        {item.label}
                        <motion.span
                          className="position-absolute bottom-0 left-0 right-0"
                          initial={{ scaleX: isActive(item.path) ? 1 : 0 }}
                          whileHover={{ scaleX: 1 }}
                          transition={{ duration: 0.175, ease: 'easeOut' }}
                          style={{ transformOrigin: 'left', backgroundColor: darkMode ? '#93c5fd' : '#1F6AE1', height: '2px' }}
                        />
                      </Link>
                  </motion.li>
                ))}

              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Desktop Navigation: Who we are, What we do, Case Studies, Insights, Careers, Contact */}
        <div className="d-none d-lg-flex navbar-nav ms-auto align-items-center" style={{ gap: '22px' }}>
          {/* Who we are */}
          {mainNavItems[0] && (
            <Link 
              key={mainNavItems[0].path}
              className={`nav-link position-relative ${isActive(mainNavItems[0].path) ? 'active fw-bold' : ''}`}
              to={mainNavItems[0].path}
              style={{ 
                textDecoration: 'none', 
                color: isActive(mainNavItems[0].path) ? navAccent : navText,
                fontWeight: isActive(mainNavItems[0].path) ? '600' : '400',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                letterSpacing: '0.02em',
                transition: 'color 0.4s ease'
              }}
              onMouseEnter={(e) => {
                if (!isActive(mainNavItems[0].path)) {
                  e.target.style.color = navAccent;
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive(mainNavItems[0].path)) {
                  e.target.style.color = navText;
                }
              }}
            >
              {mainNavItems[0].label}
              <motion.span
                className="position-absolute bottom-0 left-0 right-0"
                initial={{ scaleX: isActive(mainNavItems[0].path) ? 1 : 0 }}
                animate={{ scaleX: isActive(mainNavItems[0].path) ? 1 : 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.175, ease: 'easeOut' }}
                style={{ 
                  transformOrigin: 'left', 
                  backgroundColor: navAccent,
                  height: '2px',
                  transition: 'background-color 0.4s ease'
                }}
              />
            </Link>
          )}

          {/* What we do — desktop hover dropdown */}
          <div
            className="nav-services-dropdown"
            onMouseEnter={() => setServicesHover(true)}
            onMouseLeave={() => setServicesHover(false)}
          >
            <button
              type="button"
              className="nav-link position-relative border-0 bg-transparent nav-services-trigger"
              aria-haspopup="true"
              aria-expanded={servicesHover}
              style={{
                color: servicesHover || isServicesActive ? navAccent : navText,
                fontWeight: isServicesActive ? '600' : '400',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                letterSpacing: '0.02em',
                transition: 'color 0.4s ease',
              }}
            >
              What we do
              <span className="nav-services-chevron" aria-hidden="true">▾</span>
              <motion.span
                className="position-absolute bottom-0 start-0 end-0"
                initial={{ scaleX: 0 }}
                animate={{ scaleX: servicesHover || isServicesActive ? 1 : 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.175, ease: 'easeOut' }}
                style={{
                  transformOrigin: 'left',
                  backgroundColor: navAccent,
                  height: '2px',
                }}
              />
            </button>

            <AnimatePresence>
              {servicesHover && (
                <div className="nav-services-menu-wrap">
                  <motion.div
                    className="nav-services-menu"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    data-theme={darkMode ? 'dark' : 'light'}
                  >
                  {serviceItems.map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      className={`nav-services-menu-item ${isActive(service.path) ? 'active' : ''}`}
                      onClick={() => setServicesHover(false)}
                    >
                      <span className="nav-services-menu-label">{service.label}</span>
                      <span className="nav-services-menu-desc">{service.description}</span>
                    </Link>
                  ))}
                  <Link
                    to="/services"
                    className="nav-services-menu-all"
                    onClick={() => setServicesHover(false)}
                  >
                    All Services →
                  </Link>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>
          </div>

          {/* Case Studies, Insights, Careers, Contact */}
          {mainNavItems.slice(1).map((item) => (
            <Link 
              key={item.path}
              className={`nav-link position-relative ${
                isActive(item.path) ? 'active fw-bold' : ''
              }`}
              to={item.path}
              style={{ 
                textDecoration: 'none', 
                color: isActive(item.path) ? navAccent : navText,
                fontWeight: isActive(item.path) ? '600' : '400',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
                letterSpacing: '0.02em',
                transition: 'color 0.4s ease'
              }}
              onMouseEnter={(e) => {
                if (!isActive(item.path)) {
                  e.target.style.color = navAccent;
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive(item.path)) {
                  e.target.style.color = navText;
                }
              }}
            >
              {item.label}
              <motion.span
                className="position-absolute bottom-0 left-0 right-0"
                initial={{ scaleX: isActive(item.path) ? 1 : 0 }}
                animate={{ scaleX: isActive(item.path) ? 1 : 0 }}
                whileHover={{ scaleX: 1 }}
                transition={{ duration: 0.175, ease: 'easeOut' }}
                style={{ 
                  transformOrigin: 'left', 
                  backgroundColor: navAccent,
                  height: '2px',
                  transition: 'background-color 0.4s ease'
                }}
              />
            </Link>
          ))}

          {/* Theme toggle (desktop) */}
          <button
            type="button"
            className={`theme-toggle-btn ${darkMode ? 'is-dark' : 'is-light'}`}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            onClick={toggleTheme}
          >
            {darkMode ? <FiMoon aria-hidden="true" /> : <FiSun aria-hidden="true" />}
          </button>

        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar;


